import { useMemo } from 'react';

import Team from 'bringg-web/stores/teams/domain-object/team';

export type TeamsTree = {
	label: string;
	value: number;
	children: TeamsTree[];
	disabled?: boolean;
	checkable?: boolean;
};

const createTeamsTree = (teams: Team[]): TeamsTree[] => {
	const hashTeamsTable = Object.create(null);
	teams.forEach(team => (hashTeamsTable[team.id] = { label: team.name, value: team.id, children: [] }));
	const teamsTree = [];
	teams.forEach(team => {
		if (team.parent_team_id && hashTeamsTable[team.parent_team_id] && team.id !== team.parent_team_id)
			hashTeamsTable[team.parent_team_id].children.push(hashTeamsTable[team.id]);
		else teamsTree.push(hashTeamsTable[team.id]);
	});
	return teamsTree;
};

type UseTeamsTreeDataOptions = {
	selectedTeamIds?: number[];
	unknownTeamLabel?: string;
};

const useTeamsTreeData = (fetchedTeams: Team[] = [], options?: UseTeamsTreeDataOptions) => {
	const baseTree = useMemo(() => createTeamsTree(fetchedTeams), [fetchedTeams]);

	return useMemo(() => {
		if (!options?.selectedTeamIds || options.selectedTeamIds.length === 0) {
			return baseTree;
		}

		const teamsSet = new Set(fetchedTeams.map(team => team.id));
		const unknownTeams = options.selectedTeamIds
			.filter(teamId => !teamsSet.has(teamId))
			.map(teamId => ({
				label: `${options.unknownTeamLabel || 'Unknown Team'} (${teamId})`,
				value: teamId,
				children: []
			}));

		return unknownTeams.length > 0 ? [...baseTree, ...unknownTeams] : baseTree;
	}, [baseTree, fetchedTeams, options?.selectedTeamIds, options?.unknownTeamLabel]);
};

export default useTeamsTreeData;
